import React, { useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "../../../ui/drawer";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
} from "../../../ui/select";
import { Textarea } from "../../../ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../../ui/alert-dialog";
import { bankCategory, memberGradeDatas } from "../../../common/data";
import {
  getDepositAccount,
  getRecommendList,
} from "../../../cache/commonData";
import { ValueCell } from "../../../common/CustomCell";

// 관리자 입출금부분
const UserBalanceController = ({ reviseUserBalance, values, managerId }) => {
  const [depositValue, setDepositValue] = useState(0);
  const [withdrawalValue, setWithdrawalValue] = useState(0);

  const handleClickReviseUserBalance = (isDeposit) => {
    const userId = values.id;
    const amount = isDeposit ? depositValue : withdrawalValue;

    if (amount < 1) {
      toast.error("0원보다 큰 수를 입력해주세요");
      return;
    }

    reviseUserBalance({
      variables: {
        managerId,
        userId,
        isDeposit,
        value: amount,
      },
    });

    if (isDeposit) setDepositValue(0);
    else setWithdrawalValue(0);
  };

  const formatCurrency = (value) => {
    const num = parseInt(value);
    if (isNaN(num) || num < 0) return 0;
    return num;
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold border-b pb-2">관리자 입/출금</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-5">
        <div className="space-y-1.5">
          <Label>잔고</Label>
          <div className="flex items-center gap-1 h-9 px-3 border rounded-md bg-muted">
            <span>&#8361;</span>
            <ValueCell value={values.userBalance ?? 0} type={0} />
          </div>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1.5">
            <Label>추가입금금액</Label>
            <Input
              type="number"
              min={0}
              value={depositValue}
              onChange={(e) => setDepositValue(formatCurrency(e.target.value))}
            />
          </div>
          <Button onClick={() => handleClickReviseUserBalance(true)}>
            실행
          </Button>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1.5">
            <Label>추가출금금액</Label>
            <Input
              type="number"
              min={0}
              value={withdrawalValue}
              onChange={(e) => setWithdrawalValue(formatCurrency(e.target.value))}
            />
          </div>
          <Button onClick={() => handleClickReviseUserBalance(false)}>
            실행
          </Button>
        </div>
      </div>
    </div>
  );
};

const MemberListForm = (props) => {
  const {
    values,
    onChange,
    isOpen,
    setIsOpen,
    handleOnFinish,
    isAddNewMember,
    managerId,
    reviseUserBalance,
    onDeleteUser,
  } = props;

  const recommendList = getRecommendList().map((item) => ({
    value: item.recommendCode,
    label: item.recommendCode,
  }));

  const depositAccountList = getDepositAccount().map((item) => ({
    value: item.id,
    label: item.accountName,
  }));

  const isCustomFee = values.isCustomFeeSettings;

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    onChange((prev) => ({
      ...prev,
      [name]: type === "number" ? (value === "" ? "" : Number(value)) : value,
    }));
  };

  const handleFieldChange = (name, value) => {
    onChange((prev) => {
      const next = { ...prev, [name]: value };
      // 옵션 최대계약수 변경 시 매도계약수 자동 계산
      if (name === "option_maxAvailableCount") {
        next.optionSellCount = Math.floor(value / 5);
      }
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleOnFinish(values);
  };

  const handleOpenChange = (open) => {
    setIsOpen(open);
  };

  return (
    <Drawer direction="right" open={isOpen} onOpenChange={handleOpenChange}>
      <DrawerContent className="inset-y-0 right-0 left-auto w-[720px] max-w-[90vw] rounded-none">
        <DrawerHeader className="border-b">
          <DrawerTitle>
            {isAddNewMember ? "신규회원등록" : "회원정보수정"}
          </DrawerTitle>
        </DrawerHeader>

        <form
          onSubmit={handleSubmit}
          className="p-6 overflow-y-auto flex-1 space-y-6 [&_input:not([readonly]):not(:disabled)]:bg-white [&_textarea]:bg-white"
        >
          {/* 기본정보 */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold border-b pb-2">기본정보</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-5 [&>div]:space-y-1.5">
              <div>
                <Label htmlFor="id">아이디</Label>
                <div className="flex items-center gap-1">
                  <Input
                    id="id"
                    name="id"
                    value={values.id ?? ""}
                    onChange={handleChange}
                    readOnly={!isAddNewMember}
                    className={!isAddNewMember ? "bg-muted" : ""}
                  />
                  {!isAddNewMember && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0 h-9 w-9"
                      onClick={() => {
                        if (values.id) {
                          navigator.clipboard
                            .writeText(values.id)
                            .then(() =>
                              toast.success(
                                `아이디 ${values.id}가 복사되었습니다`
                              )
                            )
                            .catch(() => toast.error("복사에 실패했습니다"));
                        }
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="userName">이름</Label>
                <Input
                  id="userName"
                  name="userName"
                  value={values.userName ?? ""}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label htmlFor="userPw">패스워드</Label>
                <Input
                  id="userPw"
                  name="userPw"
                  value={values.userPw ?? ""}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label htmlFor="phoneNo">휴대폰</Label>
                <Input
                  id="phoneNo"
                  name="phoneNo"
                  value={values.phoneNo ?? ""}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label htmlFor="email">이메일</Label>
                <Input
                  id="email"
                  name="email"
                  value={values.email ?? ""}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label htmlFor="birthDate">생년월일 (선택)</Label>
                <Input
                  id="birthDate"
                  name="birthDate"
                  value={values.birthDate ?? ""}
                  onChange={handleChange}
                  placeholder="YYYYMMDD 또는 YYYY-MM-DD"
                  maxLength={10}
                />
              </div>
              <div>
                <Label>추천인</Label>
                <Select
                  value={values.recommendCode ?? ""}
                  onValueChange={(v) => handleFieldChange("recommendCode", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {recommendList.map((opt) => (
                      <SelectItem
                        key={opt.value}
                        value={String(opt.value)}
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!isAddNewMember && (
                <div>
                  <Label>계정상태</Label>
                  <Select
                    value={String(values.userState ?? 1)}
                    onValueChange={(v) =>
                      handleFieldChange("userState", Number(v))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">블랙</SelectItem>
                      <SelectItem value="1">정상</SelectItem>
                      <SelectItem value="2">거래정지</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>등급(권한)</Label>
                <Select
                  value={String(values.authority ?? 0)}
                  onValueChange={(v) =>
                    handleFieldChange("authority", Number(v))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {memberGradeDatas
                      .filter((item) => item.value !== 3)
                      .map((opt) => (
                        <SelectItem
                          key={opt.value}
                          value={String(opt.value)}
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>출금은행</Label>
                <Select
                  value={String(values.userAccountBank ?? 0)}
                  onValueChange={(v) =>
                    handleFieldChange("userAccountBank", Number(v))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {bankCategory.map((group) => (
                      <SelectGroup key={group.label}>
                        <SelectLabel>{group.label}</SelectLabel>
                        {group.options.map((opt) => (
                          <SelectItem
                            key={opt.value}
                            value={String(opt.value)}
                          >
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="userAccountNo">출금계좌</Label>
                <Input
                  id="userAccountNo"
                  name="userAccountNo"
                  value={values.userAccountNo ?? ""}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label htmlFor="userAccountOwner">출금예금주</Label>
                <Input
                  id="userAccountOwner"
                  name="userAccountOwner"
                  value={values.userAccountOwner ?? ""}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label>입금계좌</Label>
                <Select
                  value={String(values.deposit_accountId ?? 1)}
                  onValueChange={(v) =>
                    handleFieldChange("deposit_accountId", Number(v))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {depositAccountList.map((opt) => (
                      <SelectItem
                        key={opt.value}
                        value={String(opt.value)}
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-full">
                <Label htmlFor="memo">메모</Label>
                <Textarea
                  id="memo"
                  name="memo"
                  value={values.memo ?? ""}
                  onChange={handleChange}
                  rows={4}
                />
              </div>
            </div>
          </div>

          {!isAddNewMember && (
            <>
              {/* 관리자 입/출금 */}
              <UserBalanceController
                managerId={managerId}
                reviseUserBalance={reviseUserBalance}
                values={values}
              />

              {/* 개별 수수료 설정 */}
              <div className="flex items-center gap-2 py-2">
                <input
                  type="checkbox"
                  id="isCustomFeeSettings"
                  checked={!!values.isCustomFeeSettings}
                  onChange={(e) =>
                    handleFieldChange("isCustomFeeSettings", e.target.checked)
                  }
                />
                <Label htmlFor="isCustomFeeSettings">
                  개별 수수료 설정
                </Label>
                <span className="text-xs text-muted-foreground">
                  (활성화 시 프리셋 일괄 변경에서 제외됩니다)
                </span>
              </div>

              {/* 거래 설정 */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold border-b pb-2">거래 설정</h3>

                {/* 국내선물 | 옵션 */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 rounded-md border p-3">
                    <span className="text-xs font-semibold">국내선물</span>
                    <div className="grid grid-cols-3 gap-2 [&>div]:space-y-1">
                      <div>
                        <Label className="text-xs text-muted-foreground">수수료</Label>
                        <Input id="domestic_fee" name="domestic_fee" type="number" step="0.0001" value={values.domestic_fee ?? ""} onChange={handleChange} disabled={!isCustomFee} className="h-8" />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">최대계약수</Label>
                        <Input id="domestic_maxAvailableCount" name="domestic_maxAvailableCount" type="number" step="1" value={values.domestic_maxAvailableCount ?? ""} onChange={handleChange} disabled={!isCustomFee} className="h-8" />
                      </div>
                      <div className="flex items-end pb-1">
                        <div className="flex items-center gap-1.5">
                          <input type="checkbox" id="isOverDomestic" checked={!!values.isOverDomestic} onChange={(e) => handleFieldChange("isOverDomestic", e.target.checked)} />
                          <Label htmlFor="isOverDomestic" className="text-xs">오버나잇</Label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 rounded-md border p-3">
                    <span className="text-xs font-semibold">옵션</span>
                    <div className="grid grid-cols-4 gap-2 [&>div]:space-y-1">
                      <div>
                        <Label className="text-xs text-muted-foreground">수수료</Label>
                        <Input id="option_fee" name="option_fee" type="number" step="0.0001" value={values.option_fee ?? ""} onChange={handleChange} disabled={!isCustomFee} className="h-8" />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">최대계약수</Label>
                        <Input id="option_maxAvailableCount" name="option_maxAvailableCount" type="number" step="1" value={values.option_maxAvailableCount ?? ""} onChange={(e) => { const val = e.target.value === "" ? "" : Number(e.target.value); handleFieldChange("option_maxAvailableCount", val); }} disabled={!isCustomFee} className="h-8" />
                      </div>
                      <div className="flex items-end pb-1">
                        <div className="flex items-center gap-1.5">
                          <input type="checkbox" id="isOverOption" checked={!!values.isOverOption} onChange={(e) => handleFieldChange("isOverOption", e.target.checked)} />
                          <Label htmlFor="isOverOption" className="text-xs">오버나잇</Label>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">매도계약수</Label>
                        <Input type="number" value={values.optionSellCount ?? ""} readOnly className="h-8 bg-muted" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* 해외선물 | 항셍 */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 rounded-md border p-3">
                    <span className="text-xs font-semibold">해외선물</span>
                    <div className="grid grid-cols-3 gap-2 [&>div]:space-y-1">
                      <div>
                        <Label className="text-xs text-muted-foreground">수수료</Label>
                        <Input id="foreign_fee" name="foreign_fee" type="number" step="0.0001" value={values.foreign_fee ?? ""} onChange={handleChange} disabled={!isCustomFee} className="h-8" />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">최대계약수</Label>
                        <Input id="foreign_maxAvailableCount" name="foreign_maxAvailableCount" type="number" step="1" value={values.foreign_maxAvailableCount ?? ""} onChange={handleChange} disabled={!isCustomFee} className="h-8" />
                      </div>
                      <div className="flex items-end pb-1">
                        <div className="flex items-center gap-1.5">
                          <input type="checkbox" id="isOverForeign" checked={!!values.isOverForeign} onChange={(e) => handleFieldChange("isOverForeign", e.target.checked)} />
                          <Label htmlFor="isOverForeign" className="text-xs">오버나잇</Label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 rounded-md border p-3">
                    <span className="text-xs font-semibold">항셍</span>
                    <div className="grid grid-cols-3 gap-2 [&>div]:space-y-1">
                      <div>
                        <Label className="text-xs text-muted-foreground">수수료</Label>
                        <Input id="hangseng_fee" name="hangseng_fee" type="number" step="0.0001" value={values.hangseng_fee ?? ""} onChange={handleChange} disabled={!isCustomFee} className="h-8" />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">최대계약수</Label>
                        <Input id="hangseng_maxAvailableCount" name="hangseng_maxAvailableCount" type="number" step="1" value={values.hangseng_maxAvailableCount ?? ""} onChange={handleChange} disabled={!isCustomFee} className="h-8" />
                      </div>
                      <div className="flex items-end pb-1">
                        <div className="flex items-center gap-1.5">
                          <input type="checkbox" id="isOverHangSeng" checked={!!values.isOverHangSeng} onChange={(e) => handleFieldChange("isOverHangSeng", e.target.checked)} />
                          <Label htmlFor="isOverHangSeng" className="text-xs">오버나잇</Label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 마이크로 */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 rounded-md border p-3">
                    <span className="text-xs font-semibold">마이크로</span>
                    <div className="grid grid-cols-3 gap-2 [&>div]:space-y-1">
                      <div>
                        <Label className="text-xs text-muted-foreground">수수료</Label>
                        <Input id="mini_foreign_fee" name="mini_foreign_fee" type="number" step="0.0001" value={values.mini_foreign_fee ?? ""} onChange={handleChange} disabled={!isCustomFee} className="h-8" />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">최대계약수</Label>
                        <Input id="mini_foreign_maxAvailableCount" name="mini_foreign_maxAvailableCount" type="number" step="1" value={values.mini_foreign_maxAvailableCount ?? ""} onChange={handleChange} disabled={!isCustomFee} className="h-8" />
                      </div>
                      <div className="flex items-end pb-1">
                        <div className="flex items-center gap-1.5">
                          <input type="checkbox" id="isOverMiniForeign" checked={!!values.isOverMiniForeign} onChange={(e) => handleFieldChange("isOverMiniForeign", e.target.checked)} />
                          <Label htmlFor="isOverMiniForeign" className="text-xs">오버나잇</Label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </form>

        <DrawerFooter className="border-t flex-row justify-between">
          <div>
            {!isAddNewMember && values.authority === 4 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">회원삭제</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>회원 삭제</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div>
                        <p>
                          아이디: <strong>{values.id}</strong>
                        </p>
                        <p>회원정보를 삭제하시겠습니까?</p>
                        <p className="text-destructive">
                          삭제시 복구가 불가능합니다.
                        </p>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>취소</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onDeleteUser(values.id)}
                    >
                      삭제
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              취소
            </Button>
            <Button type="submit" onClick={handleSubmit}>
              {isAddNewMember ? "등록" : "수정"}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default MemberListForm;
